import { device } from 'utils/device';
import { globals } from 'config/globals';
import { Controller } from 'core/Controller';
import { DepartmentType } from 'app/data/Enums';
import { SlowFpsDialog } from 'app/chrome/slowfps/SlowFpsDialog';

import { AppController } from 'app/controller/AppController';
import { Simple } from 'utils/analytics';


export class PixiController extends Controller {
	private _appController: AppController;
	//Runtime object is a Pixi v8 Application from the CDN build; the ambient
	//PIXI declarations are deliberately any-based (see src/types/globals.d.ts),
	//so `any` here is as honest as the declarations get for now:
	private _$pixi: any;
	private _stage: PIXI.Container;
	private _$backgroundContainer: PIXI.Container;
	private _$workContainers: any = { screens: null, spaces: null };
	private _$homeContainer: PIXI.Container;
	private _res: number;
	private _active: boolean = true;
	private _allowResize: boolean = true;

	private _slowFrameRate = 40;
	private _slowFrameCount = 0;
	private _fastFrameCount = 0;
	private _fixFrameCount = 0;
	private _triggerWarningAtSlowFrames = 180; //4s secs of flow frames to trigger warning

	private _resetSlowFramesAtCount = 3;
	private _allowSlowRateShow = false;

	private _allowSlowFpsCheck = false;

	private _dialog: SlowFpsDialog;

	public setAllowSlowFrameRateShow() {
		this._allowSlowRateShow = true;
	}

	public init(): void {
		this._dialog = new SlowFpsDialog(this.DOM, {
			pause: () => this._appController.pauseActive(),
			unpause: () => this._appController.unpauseActive()
		});

		let path = window.location.pathname;
		//'/dev/home...' = the dev harness routes (local-only, see config/dev.ts) — same home view, needs the same Pixi app:
		if (path == '/screens' || path == '/spaces' || path == '/systems' || path == '/' || path.indexOf('/dev/home') === 0) {
			this._createPixiApp();

			this._allowSlowFpsCheck = true;
		}
	}

	public setControllers(appController: AppController) {
		this._appController = appController;
	}

	public setAllowSlowFrameCheck(allow: boolean) {
		this._allowSlowFpsCheck = allow;
	}

	public resize(fromOrientationChange: boolean = false): void {
		//Resize the renderer to 100% height and width:

		if (!this._$pixi || (!this._allowResize && !fromOrientationChange)) {
			return;
		}

		this._res = this._getPixiResolution();
		this._$pixi.resolution = this._res;
		this._$pixi.renderer.resolution = this._res;


		let innerWidth = window.innerWidth;
		let innerHeight = window.innerHeight;

		console.log('Resize');

		this._$pixi.resize(Math.round(innerWidth * this._res), Math.round(innerHeight * this._res));

		let canvas = document.getElementById('pixi') as HTMLCanvasElement;
		canvas.style.width = innerWidth + 'px';
		canvas.style.height = innerHeight + 'px';
		canvas.setAttribute('width', Math.round(innerWidth * this._res) + '');
		canvas.setAttribute('height', Math.round(innerHeight * this._res) + '');
	}

	public wasRunning() {
		return this._active && this._$pixi;
	}

	public pause(): void {
		this._active = false;
		if (this._$pixi) {
			this._$pixi.stop();
			this._allowSlowRateShow = false;
			this.DOM.addClass(document.querySelector('#pixi'), '-hide');
		}
	}

	public unpause(): void {
		this._active = true;

		if (this._$pixi) {
			this._$pixi.start();
			this._allowSlowRateShow = true;
			this.DOM.removeClass(document.querySelector('#pixi'), '-hide');
		}
	}

	//Returns the Pixi v8 Application (see _$pixi note):
	public getPixi(): any {
		return this._$pixi;
	}

	public getBackground(): PIXI.Container {
		return this._$backgroundContainer;
	}

	public getWork(department: DepartmentType): PIXI.Container {
		if (department === DepartmentType.SCREENS) return this._$workContainers.screens;
		else if (department === DepartmentType.SPACES) return this._$workContainers.spaces;

		return this._$workContainers.systems;
	}

	public getHome(): PIXI.Container {
		return this._$homeContainer;
	}

	//Stored value was only read by the removed dead getProjectHeaderSize():
	public setProjectHeaderSize(size: any): void {}

	public setAllowResize(allow: boolean): void {
		this._allowResize = allow;
	}

	public tick() {
		if (this._allowSlowFpsCheck) {
			this._checkFrameRate();
		}
	}

	//PRIVATE
	//------------------------------------------------------------------------------------
	private _checkFrameRate() {

		if (!this._$pixi) {
			return;
		}

		if (globals.slowFpsMode) {
			if (this._$pixi.ticker.FPS > this._slowFrameRate) {
				this._fixFrameCount++;
				if (this._fixFrameCount >= this._triggerWarningAtSlowFrames) {
					this._fixFrameCount = 0;
					globals.slowFpsMode = false;
					Simple.event('exit_low_frame_rate_mode ', {
						isMobile: device.isMobile,
						isSafari: device.isSafari
					});
				}
			} else if (this._fixFrameCount > 0) {
				this._fixFrameCount--;
			}

			return;
		}

		if (this._$pixi.ticker.FPS <= this._slowFrameRate) {
			this._slowFrameCount++;
			this._fastFrameCount = 0;

			if (this._slowFrameCount >= this._triggerWarningAtSlowFrames && this._allowSlowRateShow) {
				this._slowFrameCount = 0;
				globals.slowFpsMode = true;

				this._dialog.show();
			}

			return;
		}

		this._fastFrameCount++;

		if (this._fastFrameCount >= this._resetSlowFramesAtCount) {
			if (this._slowFrameCount > 0) {
				this._slowFrameCount--;
			}

			this._fastFrameCount = 0;
		}
	}

	private _createPixiApp(): void {
		//Create a PIXI app to render all the big full screen bits:
		this._res = this._getPixiResolution();
		this._createAppAsync();
	}

	private _init() {
		this._$pixi.ticker.minFPS = 60;

		//Add to the view:
		let canvas = this._$pixi.canvas || this._$pixi.view;
		document.body.appendChild(canvas);

		this.resize();
		//Add the section conatiners for easier layering:
		this._$backgroundContainer = new PIXI.Container();
		this._$backgroundContainer.interactive = false;
		this._$backgroundContainer.interactiveChildren = false;
		this._$backgroundContainer.visible = false;

		this._$homeContainer = new PIXI.Container();
		this._$homeContainer.name = 'HOME CONTAINER';
		this._$homeContainer.cullable = true;
		this._$homeContainer.interactive = false;
		this._$homeContainer.interactiveChildren = false;

		this._$workContainers.screens = new PIXI.Container();
		this._$workContainers.screens.name = 'SCREENS CONTAINER';
		this._$workContainers.screens.interactive = false;
		this._$workContainers.screens.interactiveChildren = false;
		this._$workContainers.screens.visible = false;
		this._$workContainers.screens.renderable = false;

		this._$workContainers.spaces = new PIXI.Container();
		this._$workContainers.spaces.name = 'SPACES CONTAINER';
		this._$workContainers.spaces.interactive = false;
		this._$workContainers.spaces.interactiveChildren = false;
		this._$workContainers.spaces.visible = false;

		this._$workContainers.systems = new PIXI.Container();
		this._$workContainers.systems.name = 'SYSTEMS CONTAINER';
		this._$workContainers.systems.interactive = false;
		this._$workContainers.systems.interactiveChildren = false;
		this._$workContainers.systems.visible = false;


		this._stage.addChild(
			this._$backgroundContainer,
			this._$homeContainer,
			this._$workContainers.spaces,
			this._$workContainers.screens,
			this._$workContainers.systems
		);
	}

	private async _createAppAsync() {
		this._getPixiResolution();


		this._$pixi = new PIXI.Application({});

		await this._$pixi.init({
			resizeTo: window,
			background: '#000000',
			backgroundAlpha: 1,
			resolution: this._getPixiResolution(),
			transparent: true,
			width: window.innerWidth * this._res,
			height: window.innerHeight * this._res,
			view: document.getElementById('pixi'),
			antialias: false,
			useContextAlpha: false,
			powerPreference: 'high-performance',
			premultipliedAlpha: true,
			preserveDrawingBuffer: true,
			preferWebGLVersion: 2,
			eventFeatures: {
				move: false,
				globalMove: false,
				click: false,
				wheel: false
			}
		});

		Object.assign(this._$pixi.renderer.events.features, {
			move: false,
			globalMove: false,
			click: false,
			wheel: false
		});

		globalThis.__PIXI_APP__ = this._$pixi;
		this._stage = this._$pixi.stage;

		//Debug FPS/frame-time/memory overlay, opt-in via ?stats in the URL.
		//Uses the mrdoob stats.js CDN global (/js/stats.min.js, loaded by
		//common/js.blade.php) instead of the old bundled pixi-stats (which
		//dragged the whole Pixi v5 engine into the bundle). Same DOM contract:
		//a div#stats on <body>, placement/visibility owned by the div#stats
		//rules in sass/_override.scss — hence the inline-style wipe below.
		//Panels: FPS / MS / MB (click to cycle); the pixi-stats DC/TC
		//(draw-call/texture-count) panels are gone with the v5 bundle.
		if (new URLSearchParams(location.search).has('stats')) {
			const stats = new Stats();
			stats.dom.id = 'stats';
			stats.dom.style.cssText = ''; //site CSS owns position/opacity/z-index
			document.body.appendChild(stats.dom);
			this._$pixi.ticker.add(() => stats.update());
		}

		const canvas = document.querySelector('.extrawebgl') as HTMLCanvasElement;
		// Initialize the GL context
		canvas.getContext('webgl');


		this._init();
	}

	//Hi-res assets should not be loaded onto mobile, so add  logic here
	//Also may need to load low res onto Android devices:
	private _getPixiResolution(): number {


		let resolution = window.innerWidth <= 1600 && window.devicePixelRatio ? window.devicePixelRatio : 1;
		if (resolution > 2) {
			resolution = 2;
		}
		return resolution;
	}
}
